import { useEffect, useState } from 'react';

const HEADER_SELECTOR = '.header-cross-fade';

/**
 * Height of the site header's block of header colour, tracked while `active`.
 *
 * The search pane is `position: fixed` and paints the same colour at the top of the viewport, so
 * its head has to be exactly as tall as that block or the two meet in a visible step. CSS cannot
 * size one from the other — the pane is not a descendant of the header — so the number is measured
 * and handed to the stylesheet as a custom property.
 *
 * Deliberately not `useSiteHeaderHeight`, which measures once on mount and again on window resize.
 * That is enough for the float bars that read it: they mount with the page and are positioned from
 * a header that has settled by the time they matter. This surface opens long after mount, and the
 * header settles late — on SFL, whose brand row carries a wide sponsor lockup, the mount-time
 * measurement read 507px against a header that ends up 160px, and the pane's head was drawn to it.
 *
 * So: measured when the surface opens, and kept current by a `ResizeObserver` on the header for as
 * long as it stays open. Guarded for `ResizeObserver` being absent — not every test or SSR
 * environment provides one — in which case the open-time measurement stands on its own.
 *
 * @param active whether the surface is on screen. Returns 0 before the first measurement, when
 *   there is no header on the page, and on the server; the stylesheet carries its own fallback.
 */
export function useHeaderBlockHeight(active: boolean): number {
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (!active) {
      return undefined;
    }
    const header = document.querySelector(HEADER_SELECTOR);
    if (!header) {
      return undefined;
    }

    const measure = () => setHeight(header.getBoundingClientRect().height);
    measure();

    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(measure);
    observer.observe(header);
    return () => observer.disconnect();
  }, [active]);

  return height;
}
